import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Search, Navigation, Filter, X, RefreshCw, Bell, Plus, Globe } from "lucide-react";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";
import ATMDetailSheet from "@/components/ATMDetailSheet";
import NotificationsPane, { useUnreadCount } from "@/components/NotificationsPane";
import LocationPrompt from "@/components/LocationPrompt";
import { useCountries, findCountry } from "@/lib/useCountries";
import { getMapStyle } from "@/lib/mapStyles";

import { HelpButton } from "@/components/HelpOverlay";
import { MAP_HELP } from "@/utils/helpContent";
import { getStatusDisplay } from "@/utils/statusDisplay";
import { playPop } from "@/lib/pop";
import { TRUSTED_CIRCLE_PALETTE } from "@/lib/trustedCirclePalette";

// Register the pmtiles:// protocol handler exactly once per process. Safe to
// call from module scope — the constructor is a no-op if it's already been
// mounted on the same protocol string. When the R2-hosted PMTiles file goes
// live, `mapStyles.js` just points the source at `pmtiles://…` and this
// handler transparently handles ranged HTTP reads.
if (!maplibregl.__pmtilesProtocolRegistered) {
  const protocol = new Protocol();
  maplibregl.addProtocol("pmtiles", protocol.tile);
  maplibregl.__pmtilesProtocolRegistered = true;
}

const STATUS_COLORS = {
  working: "#00FF94",
  out_of_service: "#FF3B30",
  out_of_cash: "#FFCC00",
  unknown: "#71717A",
};

/** Haversine distance between two (lat, lng) points in kilometres. */
function distanceKm(aLat, aLng, bLat, bLng) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Hex → "r,g,b" for embedding in rgba(). Returns "113,113,122" (grey) on bad input.
function hexToRgb(hex) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || "");
  if (!m) return "113,113,122";
  return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
}

/**
 * Build the DOM element for a MapLibre marker. Preserves the exact marker
 * visuals (rotated teardrop, group count badge, deposit pip) that we shipped
 * with Leaflet — the CSS in App.css doesn't need to change.
 *
 * Marker color reflects WITHDRAW status when present (primary use case). The
 * small deposit pip carries the deposit signal independently. Group marker
 * uses group-level rollup so any working sibling turns the pin green.
 *
 * Layout math (Mar 2026): `.atm-marker` is 36×36 with `border: 2.5px solid`
 * — box-sizing:content-box means the ACTUAL DOM size is 41×41. Rotating a
 * 41×41 square -45° around its center puts the visual bottom-tip at local
 * (20.5, 20.5 + 20.5*√2) ≈ (20.5, 49.5). We size the wrapper 52×52 and
 * position the marker centered so the visual tip lands PRECISELY at the
 * wrapper's bottom-middle. Combined with MapLibre `anchor: "bottom"`, the
 * teardrop tip sits exactly on the ATM's lat/lng.
 */
function buildMarkerElement(atm) {
  const isGroup = (atm?.group_size || 1) > 1;
  const withdrawCap = isGroup
    ? (atm?.group_capabilities?.withdraw || atm?.capabilities?.withdraw)
    : atm?.capabilities?.withdraw;
  const groupWithdrawAvailable = isGroup && atm?.group_capabilities?.withdraw;
  const primary = withdrawCap
    ? { status: withdrawCap.status, last_report_time: withdrawCap.last_report_time }
    : (groupWithdrawAvailable
        ? { status: atm.group_status, last_report_time: atm.group_last_report_time }
        : { status: "unknown", last_report_time: null });
  const { color, opacity, freshness } = getStatusDisplay(primary);
  const isStale = freshness === "ancient" || freshness === "old" || freshness === "none";
  const displayColor = isStale ? "#71717A" : color;
  const status = primary?.status || "unknown";
  const markerClass = isStale ? "stale" : status;
  const groupSize = atm?.group_size || 1;

  const dep = isGroup
    ? (atm?.group_capabilities?.deposit || atm?.capabilities?.deposit)
    : atm?.capabilities?.deposit;
  let depColor = null;
  let depOpacity = 1;
  if (dep) {
    const depDisp = getStatusDisplay({ status: dep.status, last_report_time: dep.last_report_time });
    depColor = depDisp.color;
    depOpacity = depDisp.opacity;
  }

  const bodyRgba = `rgba(${hexToRgb(displayColor)},${opacity})`;
  const pipRgba = depColor ? `rgba(${hexToRgb(depColor)},${depOpacity})` : null;

  // Wrapper: 52×52 accommodates the rotated 41×41 marker (visual bbox
  // ~58×58 accounting for the half-diagonal, but 52×52 is enough to align
  // the tip and keep overflow reasonable). Marker offset inside chosen so
  // visual tip lands at wrapper bottom-center (26, 52).
  const wrapper = document.createElement("div");
  wrapper.className = "atm-marker-wrapper";
  // NOTE: intentionally NOT setting `position` here — MapLibre's own
  // `.maplibregl-marker { position: absolute; top:0; left:0 }` CSS class must
  // apply. If we set `position: relative` inline, it overrides MapLibre and
  // markers stack in document flow (each 52px below the previous), causing
  // the "markers displaced in the sea" bug where marker N ends up at
  // (proj.x, proj.y + N*52).
  wrapper.style.cssText = "width:52px;height:52px;cursor:pointer;";

  const body = document.createElement("div");
  body.className = `atm-marker ${markerClass}`;
  // Marker DOM is 41×41 (36 + 2*2.5 border). Center it horizontally in the
  // 52-wide wrapper (left = (52-41)/2 = 5.5) and offset vertically so the
  // rotated visual tip at local y=49.5 lands at wrapper y=52 → top = 2.5.
  body.style.cssText = `background:${bodyRgba} !important;position:absolute;top:2.5px;left:5.5px;`;

  const inner = document.createElement("span");
  inner.className = "atm-marker-inner";
  inner.textContent = "$";
  body.appendChild(inner);

  if (pipRgba) {
    const pip = document.createElement("span");
    pip.className = "atm-marker-deposit";
    pip.style.background = pipRgba;
    pip.title = "Deposit";
    body.appendChild(pip);
  }
  if (groupSize > 1) {
    const badge = document.createElement("span");
    badge.className = "atm-marker-count";
    badge.textContent = String(groupSize);
    body.appendChild(badge);
  }
  wrapper.appendChild(body);
  return wrapper;
}

/** Blue "you are here" dot element. */
function buildUserLocationElement() {
  const el = document.createElement("div");
  el.style.cssText = [
    "width:16px", "height:16px",
    "background:#3B82F6",
    "border:3px solid white",
    "border-radius:50%",
    "box-shadow:0 0 10px rgba(59,130,246,0.5)",
  ].join(";");
  return el;
}

export default function MapPage() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { countries } = useCountries();

  const userCountry = user?.country_code || "JM";
  const [countryFilter, setCountryFilter] = useState(userCountry);
  useEffect(() => {
    setCountryFilter(userCountry);
  }, [userCountry]);
  const activeCountry = useMemo(() => findCountry(countries, countryFilter), [countries, countryFilter]);

  const [atms, setAtms] = useState([]);
  const [totalAtmCount, setTotalAtmCount] = useState(null);
  const [selectedAtm, setSelectedAtm] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [userPos, setUserPos] = useState(null);
  const [locationStatus, setLocationStatus] = useState("pending");
  const [locationPromptOpen, setLocationPromptOpen] = useState(false);
  const [locationErrorCode, setLocationErrorCode] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifOpen, setNotifOpen] = useState(false);
  const { count: unreadCount, refresh: refreshUnread } = useUnreadCount(user);

  // The user's chosen map style. Falls back to "dark" when the field is
  // absent (all users prior to Mar 2026). Kept in state so the map can
  // hot-swap styles the instant the Profile toggle flips.
  const mapStyleName = user?.map_style_preference === "light" ? "light" : "dark";

  // ── MapLibre refs ─────────────────────────────────────────────────────
  const containerRef = useRef(null);
  const mapRef = useRef(null);          // maplibregl.Map instance
  const markersRef = useRef(new Map()); // id → { marker, sig } for diffing
  const userMarkerRef = useRef(null);   // blue "you are here" marker
  const styleRef = useRef(mapStyleName);
  // Whether the map's style has finished loading — we can only add sources
  // and layers after `load` fires (or after a `setStyle` completes).
  const mapReadyRef = useRef(false);
  // Trusted-circle radiuses the current user belongs to. Cached across
  // ATM selections so we don't refetch every time the user opens a sheet.
  const [myCircles, setMyCircles] = useState([]);
  // Ref: previous `last_report_at` per ATM id. Enables the LIVE ACTIVITY
  // PULSE by diffing incoming atms payload against last-known timestamps.
  // Only pulse when timestamp becomes newer AND report is < 60s old.
  const lastReportAtRef = useRef(new Map());
  // Concurrency cap for live pulses — hard limit of 10 simultaneous.
  const activePulsesRef = useRef(new Set());
  // Suppress the live-pulse for a specific ATM id briefly right after the
  // user's own fly-in animation lands on it — avoids double-pulsing the
  // same event from two directions.
  const pulseSuppressUntilRef = useRef(new Map());

  // Selected-atm handler stored via ref so marker click handlers don't need
  // to rebind on every parent render. Also nudges the map upward via
  // `easeTo` padding so the ATM (and any Trusted Circle radius overlay
  // around it) sits in the top half of the viewport, unobstructed by the
  // bottom detail sheet. Padding is reset on sheet close.
  const applySheetPadding = useCallback((atm) => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current || !atm) return;
    if (typeof atm.longitude !== "number" || typeof atm.latitude !== "number") return;
    // ~45% of viewport height matches the sheet's max-height (75vh) closely
    // enough while leaving breathing room for the largest alert radii
    // (10km) at the mapʼs default zoom. Capped so ultra-tall screens
    // don't over-pan.
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const bottomPad = Math.min(420, Math.round(vh * 0.45));
    map.easeTo({
      center: [atm.longitude, atm.latitude],
      padding: { top: 0, right: 0, bottom: bottomPad, left: 0 },
      duration: 550,
      essential: true,
    });
  }, []);

  const clearSheetPadding = useCallback(() => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) return;
    map.easeTo({
      padding: { top: 0, right: 0, bottom: 0, left: 0 },
      duration: 400,
      essential: true,
    });
  }, []);

  const selectHandlerRef = useRef((atm) => {
    setSelectedAtm(atm);
    applySheetPadding(atm);
  });
  useEffect(() => {
    selectHandlerRef.current = (atm) => {
      setSelectedAtm(atm);
      applySheetPadding(atm);
    };
  }, [applySheetPadding]);

  // Initial center — user's country default, Kingston fallback. Only used at
  // map construction time; subsequent recentres go through map.flyTo().
  const initialView = useMemo(() => {
    if (activeCountry?.default_center) {
      return { center: activeCountry.default_center, zoom: activeCountry.default_zoom || 11 };
    }
    return { center: [18.01, -76.79], zoom: 13 };
  }, [activeCountry]);

  // ── Map init ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: getMapStyle(styleRef.current),
      // MapLibre uses [lng, lat] — reverse the [lat, lng] we store elsewhere.
      center: [initialView.center[1], initialView.center[0]],
      zoom: initialView.zoom,
      attributionControl: false,
      // Lower cooperativeGestures/pitchWithRotate defaults keep the map
      // simple and mobile-friendly. Pitch is disabled by default (we render
      // 2D vector tiles). Users can still two-finger pinch to rotate/zoom.
      pitchWithRotate: false,
      dragRotate: false,
      touchZoomRotate: true,
      maxPitch: 0,
    });
    map.addControl(new maplibregl.AttributionControl({ compact: true, customAttribution: "© OpenFreeMap © OpenStreetMap" }), "bottom-right");
    mapRef.current = map;

    // Surface silent tile-fetch failures — MapLibre catches these internally
    // and logs to console but never fires a user-visible signal. Common
    // culprits: R2 CORS misconfig, expired signed URLs, PMTiles archive
    // deleted from origin. We only toast once per session so the user
    // isn't spammed if every tile fails.
    let tileErrorToasted = false;
    map.on("error", (e) => {
      const url = e?.error?.url || e?.source?.url || "";
      const msg = e?.error?.message || "";
      const isTileFetch = url.includes("pmtiles") || url.includes("r2.dev") || msg.toLowerCase().includes("cors");
      if (isTileFetch && !tileErrorToasted) {
        tileErrorToasted = true;
        toast.error("Map tiles failed to load — check your connection", { duration: 5000 });
        // Reset the flag after 30s so a genuinely transient failure can
        // re-notify if it comes back later.
        setTimeout(() => { tileErrorToasted = false; }, 30000);
      }
    });

    // Wait for style to load before we start adding markers.
    map.on("load", () => {
      mapReadyRef.current = true;
      // Trusted-circle radius overlay (Mar 2026 v2.0). We add ONE GeoJSON
      // source that we later populate with concentric circle polygons
      // centred on the currently-selected ATM. Fill for the tint, line for
      // the border. Both layers are invisible until we push features into
      // the source.
      //
      // Feb 2026 update: paint is now data-driven — each circle feature
      // carries its own `color` property (picked from TRUSTED_CIRCLE_PALETTE
      // in ATMDetailSheet/MapPage), which lets users distinguish overlapping
      // circles when they belong to multiple Trusted Circles. Line width +
      // fill opacity bumped so radii are visible on the LIGHT-mode cream
      // basemap where the previous solid neon green got washed out.
      if (!map.getSource("circle-radius-src")) {
        map.addSource("circle-radius-src", {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
        map.addLayer({
          id: "circle-radius-fill",
          type: "fill",
          source: "circle-radius-src",
          paint: {
            "fill-color": ["get", "color"],
            "fill-opacity": 0.09,
          },
        });
        map.addLayer({
          id: "circle-radius-line",
          type: "line",
          source: "circle-radius-src",
          paint: {
            "line-color": ["get", "color"],
            "line-width": 2.5,
            "line-opacity": 0.85,
            "line-dasharray": [3, 2],
          },
        });
      }
    });

    // If the user hot-swaps map styles (setStyle), MapLibre drops all
    // sources+layers. Re-attach on `styledata` events, but only after the
    // initial load — otherwise we duplicate the addSource call above.
    map.on("styledata", () => {
      if (!mapReadyRef.current) return;
      if (map.getSource("circle-radius-src")) return;
      map.addSource("circle-radius-src", {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
      map.addLayer({
        id: "circle-radius-fill", type: "fill", source: "circle-radius-src",
        paint: { "fill-color": ["get", "color"], "fill-opacity": 0.09 },
      });
      map.addLayer({
        id: "circle-radius-line", type: "line", source: "circle-radius-src",
        paint: { "line-color": ["get", "color"], "line-width": 2.5, "line-opacity": 0.85, "line-dasharray": [3, 2] },
      });
    });

    // Snapshot the ref for use inside cleanup — React's exhaustive-deps
    // check warns that markersRef.current may have changed by cleanup time,
    // but we deliberately want the LIVE marker set at unmount, not the one
    // captured at effect time. Safe here because this cleanup only runs
    // when MapPage unmounts (empty dep array below).
    const markersSnapshot = markersRef;
    return () => {
      for (const { marker } of markersSnapshot.current.values()) marker.remove();
      markersSnapshot.current.clear();
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
    };
    // Intentionally empty deps — we want ONE map instance for the lifetime
    // of the page. `initialView` is captured at mount and shouldn't
    // trigger re-init on subsequent country changes (we use flyTo instead).
  }, []);

  // ── Hot-swap style when user toggles Dark/Light ──────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    if (styleRef.current === mapStyleName) return;
    styleRef.current = mapStyleName;
    // setStyle re-uses the tile source, so this is a very fast paint swap.
    mapRef.current.setStyle(getMapStyle(mapStyleName));
  }, [mapStyleName]);

  // ── ATM fetching + polling ───────────────────────────────────────────
  const queryRef = useRef({ userPos: null, search: "", filter: "all", country: "" });
  useEffect(() => {
    queryRef.current = { userPos, search, filter, country: countryFilter };
  }, [userPos, search, filter, countryFilter]);

  const fetchAtms = useCallback(async () => {
    const { search: s, filter: f, country: c } = queryRef.current;
    try {
      const params = {};
      if (s) params.search = s;
      if (f !== "all") params.status_filter = f;
      if (c) params.country_code = c;
      const res = await axios.get(`${API}/atms`, { params });
      setAtms((prev) => {
        const next = res.data;
        if (
          prev.length === next.length &&
          prev.every((p, i) => {
            const n = next[i];
            const pWd = p?.capabilities?.withdraw?.status || p?.status || "";
            const nWd = n?.capabilities?.withdraw?.status || n?.status || "";
            const pDep = p?.capabilities?.deposit?.status || "";
            const nDep = n?.capabilities?.deposit?.status || "";
            const pGW = p?.group_capabilities?.withdraw?.status || p?.group_status || "";
            const nGW = n?.group_capabilities?.withdraw?.status || n?.group_status || "";
            const pGD = p?.group_capabilities?.deposit?.status || "";
            const nGD = n?.group_capabilities?.deposit?.status || "";
            const pGT = p?.group_last_report_time || p?.group_capabilities?.withdraw?.last_report_time || "";
            const nGT = n?.group_last_report_time || n?.group_capabilities?.withdraw?.last_report_time || "";
            return n &&
              p.id === n.id &&
              pWd === nWd &&
              pDep === nDep &&
              pGW === nGW &&
              pGD === nGD &&
              pGT === nGT &&
              (p.group_size || 1) === (n.group_size || 1) &&
              (p.last_report_at || "") === (n.last_report_at || "");
          })
        ) return prev;
        return next;
      });
    } catch (err) {
      console.error("Failed to load ATMs", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTotalCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/atms`);
      setTotalAtmCount(Array.isArray(res.data) ? res.data.length : null);
    } catch { /* soft-fail */ }
  }, []);

  useEffect(() => { fetchAtms(); }, [userPos, search, filter, countryFilter, fetchAtms]);

  useEffect(() => {
    fetchTotalCount();
    const totalInterval = setInterval(fetchTotalCount, 5 * 60 * 1000);
    const refreshInterval = setInterval(fetchAtms, 30000);
    const onFocus = () => { fetchAtms(); fetchTotalCount(); };
    const onVisibility = () => {
      if (document.visibilityState === "visible") onFocus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(totalInterval);
      clearInterval(refreshInterval);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchAtms, fetchTotalCount]);

  // ── GPS ──────────────────────────────────────────────────────────────
  const requestLocation = useCallback((opts = {}) => {
    const { promptOnError = false, recenter = false } = opts;
    if (!navigator.geolocation) {
      setLocationStatus("unsupported");
      setLocationErrorCode("unsupported");
      if (promptOnError) setLocationPromptOpen(true);
      return;
    }
    setLocationStatus("pending");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = [pos.coords.latitude, pos.coords.longitude];
        setUserPos(coords);
        setLocationStatus("granted");
        setLocationErrorCode(null);
        setLocationPromptOpen(false);
        if (recenter && mapRef.current) {
          mapRef.current.flyTo({ center: [coords[1], coords[0]], zoom: 14, duration: 900 });
        }
        if (user) {
          axios.post(
            `${API}/trusted/check-nearby`,
            { lat: pos.coords.latitude, lng: pos.coords.longitude },
            { withCredentials: true },
          ).catch(() => { /* silent */ });
        }
      },
      (err) => {
        const code = err?.code || 2;
        setLocationStatus(code === 1 ? "denied" : "unavailable");
        setLocationErrorCode(code);
        if (promptOnError) setLocationPromptOpen(true);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  }, [user]);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  // Retry GPS when the user comes back from Android Settings.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (locationStatus === "denied" || locationStatus === "unavailable") {
        requestLocation();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    let permStatus = null;
    let permHandler = null;
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          permStatus = status;
          permHandler = () => {
            if (status.state === "granted") requestLocation();
          };
          status.addEventListener("change", permHandler);
        })
        .catch(() => { /* ignore */ });
    }
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      if (permStatus && permHandler) {
        try { permStatus.removeEventListener("change", permHandler); } catch { /* ignore */ }
      }
    };
  }, [requestLocation, locationStatus]);

  // ── Country recentre (only when active country differs from GPS home) ──
  useEffect(() => {
    if (!mapRef.current || !activeCountry?.default_center) return;
    if (locationStatus === "granted" && countryFilter === userCountry) return;
    const [lat, lng] = activeCountry.default_center;
    mapRef.current.flyTo({ center: [lng, lat], zoom: activeCountry.default_zoom || 11, duration: 900 });
  }, [activeCountry, locationStatus, countryFilter, userCountry]);

  // ── User-location marker ─────────────────────────────────────────────
  useEffect(() => {
    if (!mapRef.current) return;
    if (!userPos) {
      if (userMarkerRef.current) {
        userMarkerRef.current.remove();
        userMarkerRef.current = null;
      }
      return;
    }
    const [lat, lng] = userPos;
    if (userMarkerRef.current) {
      userMarkerRef.current.setLngLat([lng, lat]);
    } else {
      userMarkerRef.current = new maplibregl.Marker({ element: buildUserLocationElement() })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);
    }
  }, [userPos]);

  // ── ATM markers: diff + upsert ───────────────────────────────────────
  // MapLibre `Marker`s are DOM-based. We keep a Map<id, {marker, sig}> and
  // only re-create the DOM element when the signature (status/group/etc)
  // changes. For non-changed markers we just leave them in place.
  const visibleAtms = useMemo(() => {
    // Emit one marker per (group_id || id) — see notes in the original file.
    const seen = new Set();
    const out = [];
    for (const atm of atms) {
      const key = atm.group_id || atm.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(atm);
    }
    return out;
  }, [atms]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    const nextIds = new Set();
    for (const atm of visibleAtms) {
      if (typeof atm.latitude !== "number" || typeof atm.longitude !== "number") continue;
      const id = atm.group_id || atm.id;
      nextIds.add(id);
      const wdStatus = atm?.capabilities?.withdraw?.status || atm?.group_capabilities?.withdraw?.status || atm?.status || "";
      const depStatus = atm?.capabilities?.deposit?.status || atm?.group_capabilities?.deposit?.status || "";
      const groupSize = atm.group_size || 1;
      const lastReport = atm.last_report_at || atm?.group_capabilities?.withdraw?.last_report_time || "";
      const sig = `${wdStatus}|${depStatus}|${groupSize}|${lastReport}|${atm.latitude}|${atm.longitude}`;

      const existing = markersRef.current.get(id);
      if (existing && existing.sig === sig) continue;

      // Was this a genuine STATUS change vs a brand-new marker? Only trigger
      // the pulse animation on a status flip so first-render doesn't fire
      // pulses on every marker (which would be visual noise).
      const isStatusChange = existing && existing.wdStatus && existing.wdStatus !== wdStatus;

      if (existing) {
        // In-place update path (Mar 2026). Prior remove+recreate flashed the
        // marker off the map for a paint frame after every report — users
        // saw markers "disappear" briefly. Now we mutate the existing
        // marker's DOM in place and shift its LngLat, so there's no map
        // detach/reattach and zero flicker.
        const el = existing.marker.getElement();
        // Cancel any lingering pulse-cleanup timer from a prior status flip.
        if (el.__pulseTimer) {
          clearTimeout(el.__pulseTimer);
          el.__pulseTimer = null;
        }
        // Swap the marker's visual innards with a freshly-built copy. The
        // outer wrapper (which MapLibre owns for positioning + click) stays.
        const rebuilt = buildMarkerElement(atm);
        el.replaceChildren(...rebuilt.children);
        // Keep the click handler's captured `atm` reference fresh so the
        // ATM detail sheet always opens the latest data, not a stale copy.
        el.__atm = atm;
        // Only setLngLat if it actually changed (rare — usually only on
        // admin edits). Skipping unnecessary calls keeps the render cheap.
        if (existing.lat !== atm.latitude || existing.lng !== atm.longitude) {
          existing.marker.setLngLat([atm.longitude, atm.latitude]);
        }
        if (isStatusChange) {
          const body = el.querySelector(".atm-marker");
          if (body) {
            body.classList.add("atm-marker-just-updated");
            el.__pulseTimer = setTimeout(() => {
              body.classList.remove("atm-marker-just-updated");
              el.__pulseTimer = null;
            }, 2000);
          }
        }
        existing.sig = sig;
        existing.wdStatus = wdStatus;
        existing.lat = atm.latitude;
        existing.lng = atm.longitude;
        continue;
      }

      // Brand-new marker (no prior instance for this id).
      const el = buildMarkerElement(atm);
      el.__atm = atm;
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        // Read from the element so subsequent in-place updates propagate.
        selectHandlerRef.current(el.__atm);
      });
      // No pulse on brand-new markers — that would be visual noise on
      // first paint / after a country switch. Pulse is reserved for
      // genuine status transitions on already-known ATMs.
      // Marker positioning (Mar 2026 v3): use explicit CENTER anchor + a
      // computed offset instead of `anchor: "bottom"`. During the v2 fix
      // the "bottom" anchor showed a systematic full-wrapper displacement
      // in production (markers appearing ~50 px south of their geo point).
      // Root cause suspected: MapLibre's `translate(-50%, -100%)` percent
      // transforms depend on the wrapper's laid-out size at composite time,
      // and some browsers apply it before the wrapper's inline dimensions
      // are settled → % resolves against 0 → marker sits at geo point's
      // TOP-LEFT instead of BOTTOM-CENTER.
      //
      // Using anchor:"center" + a pixel offset makes the anchor calc
      // independent of the wrapper's layout — MapLibre uses fixed pixel
      // arithmetic. The offset lifts the marker 26px so its bottom-middle
      // (where the rotated teardrop tip lands, per buildMarkerElement math)
      // sits exactly on the geo point.
      const marker = new maplibregl.Marker({
        element: el,
        anchor: "center",
        offset: [0, -26],
      })
        .setLngLat([atm.longitude, atm.latitude])
        .addTo(map);
      markersRef.current.set(id, {
        marker,
        sig,
        wdStatus,
        lat: atm.latitude,
        lng: atm.longitude,
      });
    }
    // Remove any markers that are no longer in the payload.
    for (const [id, { marker }] of markersRef.current.entries()) {
      if (!nextIds.has(id)) {
        // Clear any pending pulse-cleanup timer to avoid firing on a
        // detached node (harmless but noisy in devtools). Also keeps the
        // GC path clean — no dangling closures over removed markers.
        const el = marker.getElement();
        if (el?.__pulseTimer) {
          clearTimeout(el.__pulseTimer);
          el.__pulseTimer = null;
        }
        marker.remove();
        markersRef.current.delete(id);
      }
    }
  }, [visibleAtms]);

  // ── Deep-link: /map?atm=<id> ─────────────────────────────────────────
  useEffect(() => {
    const target = searchParams.get("atm");
    if (!target || atms.length === 0 || !mapRef.current) return;
    const match = atms.find((a) => a.id === target);
    if (!match) return;
    setSelectedAtm(match);
    applySheetPadding(match);
    const next = new URLSearchParams(searchParams);
    next.delete("atm");
    setSearchParams(next, { replace: true });
  }, [searchParams, atms, setSearchParams, applySheetPadding]);

  // Fetch the user's trusted-circle radiuses once per session. We only care
  // about ACTIVE circles (archived circles don't fire notifications).
  //
  // Each fetched circle is stamped with a stable `color` from a curated
  // palette so the map overlay + sheet legend + circle-list UI all share
  // the same colour identity for a given circle. Palette lives in
  // /app/frontend/src/lib/trustedCirclePalette.js.
  useEffect(() => {
    if (!user) { setMyCircles([]); return; }
    let cancelled = false;
    axios.get(`${API}/trusted/circles`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        const active = (res.data || [])
          .filter((c) => c.status !== "archived" && c.radius_km)
          .map((c, i) => ({ ...c, color: TRUSTED_CIRCLE_PALETTE[i % TRUSTED_CIRCLE_PALETTE.length] }));
        setMyCircles(active);
      })
      .catch(() => { /* silent — overlay just won't render */ });
    return () => { cancelled = true; };
  }, [user]);

  // Circle-radius overlay: when the user opens an ATM's detail sheet, draw
  // one dashed circle per trusted-circle they belong to, centred on that
  // ATM's coords. Each circle uses its own `radius_km` and its stamped
  // `color` so overlapping circles remain visually distinct. Users see at
  // a glance which of their circle members' notification radiuses cover
  // this ATM — a report they submit here will reach anyone whose home GPS
  // sits inside the corresponding circle.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) return;
    const src = map.getSource("circle-radius-src");
    if (!src) return;
    if (!selectedAtm || myCircles.length === 0) {
      src.setData({ type: "FeatureCollection", features: [] });
      return;
    }
    // Convert (radius_km centred on ATM) into a ~64-point polygon in lat/lng.
    // Small enough radii (<20km) — flat-earth math is accurate enough here.
    const features = myCircles.map((c) => {
      const radiusKm = c.radius_km || 5;
      const points = 64;
      const coords = [];
      const latRad = (selectedAtm.latitude * Math.PI) / 180;
      const kmPerDegLat = 110.574;
      const kmPerDegLng = 111.320 * Math.cos(latRad);
      for (let i = 0; i <= points; i++) {
        const angle = (i / points) * 2 * Math.PI;
        const dLat = (radiusKm * Math.sin(angle)) / kmPerDegLat;
        const dLng = (radiusKm * Math.cos(angle)) / kmPerDegLng;
        coords.push([selectedAtm.longitude + dLng, selectedAtm.latitude + dLat]);
      }
      return {
        type: "Feature",
        properties: { name: c.name, radius_km: radiusKm, color: c.color },
        geometry: { type: "Polygon", coordinates: [coords] },
      };
    });
    src.setData({ type: "FeatureCollection", features });
  }, [selectedAtm, myCircles]);

  // ── Live Activity Pulse + Report Fly-In (Mar 2026 v2.0) ──────────────
  // Detect on-device motion prefs once — guardrail #3 (reduced-motion) and
  // #4 (low-power devices) both surface as simplified visuals.
  const shouldReduceMotion =
    typeof window !== "undefined" &&
    (
      (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ||
      document.body?.classList?.contains("low-power-device")
    );

  // Create + attach a short-lived DOM pulse marker at [lng, lat].
  // Auto-cleans after `ttlMs`. Respects the concurrency cap (guardrail #1)
  // and reduced-motion fallback (guardrail #3/4).
  const spawnActivityPulse = useCallback((lng, lat, opts = {}) => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current) return;
    if (activePulsesRef.current.size >= 10) return; // guardrail #1
    const el = document.createElement("div");
    el.className = shouldReduceMotion ? "activity-pulse reduced" : "activity-pulse";
    if (opts.variant) el.classList.add(opts.variant);
    const marker = new maplibregl.Marker({ element: el, anchor: "center" })
      .setLngLat([lng, lat])
      .addTo(map);
    activePulsesRef.current.add(marker);
    const ttl = opts.ttlMs || (opts.variant === "impact" ? 1300 : 1900);
    setTimeout(() => {
      marker.remove();
      activePulsesRef.current.delete(marker);
    }, ttl);
  }, [shouldReduceMotion]);

  // Fly-in animation: a small dot travels from `from` to `to` over
  // `durationMs`, then triggers an impact pulse on arrival. Uses
  // requestAnimationFrame + linear interpolation on the two lng/lat pairs.
  // Cost is trivial — one marker's setLngLat called ~48 times over 800ms.
  const flyReportMarker = useCallback((from, to, durationMs = 800) => {
    const map = mapRef.current;
    if (!map || !mapReadyRef.current || !from || !to) return;
    if (shouldReduceMotion) {
      // Reduced-motion fallback: just fire the impact pulse — skip the trip
      // and the sound (audio can be as jarring as motion for some users).
      spawnActivityPulse(to[0], to[1], { variant: "impact" });
      return;
    }
    const el = document.createElement("div");
    el.className = "flying-report-dot";
    const marker = new maplibregl.Marker({ element: el, anchor: "center" })
      .setLngLat(from)
      .addTo(map);
    const t0 = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - t0) / durationMs);
      // Ease-out cubic: fast start, decelerate to target.
      const e = 1 - Math.pow(1 - t, 3);
      const lng = from[0] + (to[0] - from[0]) * e;
      const lat = from[1] + (to[1] - from[1]) * e;
      marker.setLngLat([lng, lat]);
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        marker.remove();
        spawnActivityPulse(to[0], to[1], { variant: "impact" });
        // Synthesised "pop" at the landing frame — small dopamine hit.
        // Try/caught because Safari can throw if the AudioContext state
        // shifts mid-play and we don't want a broken sound to break UX.
        try { playPop(); } catch { /* silent */ }
      }
    };
    requestAnimationFrame(step);
  }, [shouldReduceMotion, spawnActivityPulse]);

  // Pick a random point just OUTSIDE the current map viewport. Used as the
  // fly-in origin when the user's GPS is unavailable — the teardrop now
  // always animates in, giving submission the same tactile feel regardless
  // of location permissions.
  const randomOffscreenOrigin = useCallback(() => {
    const map = mapRef.current;
    if (!map) return null;
    const canvas = map.getCanvas();
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    if (!w || !h) return null;
    // 60px buffer past the edge so the teardrop starts fully off-screen.
    const buf = 60;
    const side = Math.floor(Math.random() * 4); // 0=top 1=right 2=bottom 3=left
    let x, y;
    if (side === 0) { x = Math.random() * w; y = -buf; }
    else if (side === 1) { x = w + buf; y = Math.random() * h; }
    else if (side === 2) { x = Math.random() * w; y = h + buf; }
    else { x = -buf; y = Math.random() * h; }
    const ll = map.unproject([x, y]);
    return [ll.lng, ll.lat];
  }, []);

  // Listen for the fly-in trigger dispatched by ATMDetailSheet on successful
  // report submission. Also mark this ATM as "just pulsed by us" so the
  // live-activity poll below doesn't fire a duplicate on the next tick.
  useEffect(() => {
    const handler = (e) => {
      const { atmLng, atmLat, atmId } = e.detail || {};
      if (typeof atmLng !== "number" || typeof atmLat !== "number") return;
      // Prefer GPS origin for the "I'm here, sending it" feel. If GPS is
      // off, fall back to a random point just off-screen so users still
      // get the fly-in tactile animation instead of an abrupt pulse.
      const from = userPos ? [userPos[1], userPos[0]] : randomOffscreenOrigin();
      if (from) {
        flyReportMarker(from, [atmLng, atmLat]);
      } else {
        // Both GPS AND map unproject unavailable (very rare — map not
        // ready). Still play the pop so submission feedback isn't silent.
        spawnActivityPulse(atmLng, atmLat, { variant: "impact" });
        if (!shouldReduceMotion) {
          try { playPop(); } catch { /* silent */ }
        }
      }
      if (atmId) {
        pulseSuppressUntilRef.current.set(atmId, Date.now() + 8000);
      }
    };
    window.addEventListener("taladraw:report-submitted", handler);
    return () => window.removeEventListener("taladraw:report-submitted", handler);
  }, [userPos, flyReportMarker, spawnActivityPulse, randomOffscreenOrigin, shouldReduceMotion]);

  // Live activity pulse: diff `last_report_at` on every /api/atms poll to
  // detect NEW reports since our last snapshot. Guardrail #2 (piggyback on
  // existing 30s poll — zero new network work), #5 (age gate 60s), #6
  // (off-screen culling handled by MapLibre for the DOM marker), #7 (tab
  // visibility → CSS animation auto-pauses).
  useEffect(() => {
    const now = Date.now();
    for (const atm of atms) {
      const id = atm.id;
      if (!id) continue;
      const newTs = atm.last_report_at;
      if (!newTs) continue;
      const prev = lastReportAtRef.current.get(id);
      lastReportAtRef.current.set(id, newTs);
      if (!prev) continue; // first-time seen — no pulse (would flash on load)
      if (newTs === prev) continue;
      // Age gate — only pulse if the report is < 60s old (guardrail #5).
      const reportTime = new Date(newTs).getTime();
      if (Number.isNaN(reportTime) || now - reportTime > 60_000) continue;
      // Skip if we already fly-in-pulsed this ATM in the last few seconds.
      const suppressUntil = pulseSuppressUntilRef.current.get(id) || 0;
      if (suppressUntil > now) continue;
      if (typeof atm.longitude === "number" && typeof atm.latitude === "number") {
        spawnActivityPulse(atm.longitude, atm.latitude);
      }
    }
  }, [atms, spawnActivityPulse]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAtms();
  };

  const filters = [
    { key: "all", label: "All" },
    { key: "working", label: "Working" },
    { key: "out_of_cash", label: "No Cash" },
    { key: "out_of_service", label: "Offline" },
  ];

  const nearestKm = useMemo(() => {
    if (!userPos || atms.length === 0) return null;
    let min = Infinity;
    for (const a of atms) {
      if (typeof a.latitude !== "number" || typeof a.longitude !== "number") continue;
      const d = distanceKm(userPos[0], userPos[1], a.latitude, a.longitude);
      if (d < min) min = d;
    }
    return Number.isFinite(min) ? min : null;
  }, [userPos, atms]);

  // ── Style toggle (in-map floating pill) ──────────────────────────────
  const toggleMapStyle = useCallback(async () => {
    const next = mapStyleName === "dark" ? "light" : "dark";
    // Optimistic UI — hot-swap style immediately, persist in background.
    if (mapRef.current) mapRef.current.setStyle(getMapStyle(next));
    styleRef.current = next;
    try {
      await axios.put(
        `${API}/auth/map-style`,
        { map_style_preference: next },
        { withCredentials: true }
      );
      if (setUser && user) setUser({ ...user, map_style_preference: next });
    } catch {
      toast.error("Failed to save map style");
      if (mapRef.current) mapRef.current.setStyle(getMapStyle(mapStyleName));
      styleRef.current = mapStyleName;
    }
  }, [mapStyleName, setUser, user]);

  return (
    <div className="app-screen w-screen relative" data-testid="map-page">
      {/* Floating Search Bar */}
      <div className="floating-search" data-testid="search-bar">
        <form onSubmit={handleSearch} className="relative">
          <div className="glass-card flex items-center px-4 h-12 shadow-lg">
            <Search className="w-5 h-5 text-zinc-500 flex-shrink-0" strokeWidth={1.5} />
            <input
              type="text"
              placeholder="Search ATMs, banks..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 bg-transparent border-none outline-none text-white placeholder:text-zinc-500 ml-3 text-sm"
              data-testid="search-input"
            />
            {search && (
              <button onClick={() => { setSearch(""); }} type="button" className="p-1" data-testid="clear-search-btn">
                <X className="w-4 h-4 text-zinc-500" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`ml-2 p-1.5 rounded-lg transition-colors ${showFilters ? 'bg-[#00FF94]/20 text-[#00FF94]' : 'text-zinc-500 hover:text-white'}`}
              data-testid="filter-toggle-btn"
            >
              <Filter className="w-4 h-4" strokeWidth={1.5} />
            </button>
            {user && (
              <button
                type="button"
                onClick={() => setNotifOpen(true)}
                className="ml-1 p-1.5 rounded-lg text-zinc-500 hover:text-white transition-colors relative"
                data-testid="notifications-bell-btn"
              >
                <Bell className="w-4 h-4" strokeWidth={1.5} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#FF3B30] text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="unread-count-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
            )}
          </div>
        </form>

        {showFilters && (
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1 px-1" data-testid="filter-chips">
            {filters.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  filter === f.key
                    ? "bg-[#00FF94] text-black"
                    : "glass-card text-zinc-400 hover:text-white"
                }`}
                data-testid={`filter-${f.key}`}
              >
                {f.key !== "all" && (
                  <span
                    className="inline-block w-2 h-2 rounded-full mr-1.5"
                    style={{ background: STATUS_COLORS[f.key] }}
                  />
                )}
                {f.label}
              </button>
            ))}
          </div>
        )}

        {showFilters && countries.length > 1 && (
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1 px-1" data-testid="country-chips">
            <button
              onClick={() => setCountryFilter("")}
              className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                countryFilter === ""
                  ? "bg-[#00FF94] text-black"
                  : "glass-card text-zinc-400 hover:text-white"
              }`}
              data-testid="country-chip-all"
            >
              <Globe className="inline w-3 h-3 mr-1 -mt-0.5" strokeWidth={2} />
              All
            </button>
            {countries.map((c) => (
              <button
                key={c.code}
                onClick={() => setCountryFilter(c.code)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  countryFilter === c.code
                    ? "bg-[#00FF94] text-black"
                    : "glass-card text-zinc-400 hover:text-white"
                }`}
                data-testid={`country-chip-${c.code}`}
              >
                <span className="mr-1">{c.flag}</span>
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* MapLibre canvas — one full-screen div; imperative marker mgmt above */}
      <div
        ref={containerRef}
        style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0 }}
        data-testid="maplibre-canvas"
      />

      {/* FABs */}
      <div className="fixed bottom-24 right-4 z-20 flex flex-col gap-2">
        <button
          onClick={toggleMapStyle}
          className="w-12 h-12 rounded-full glass-card flex items-center justify-center shadow-lg hover:border-white/20 transition-colors text-[11px] font-bold uppercase tracking-wider"
          data-testid="map-style-toggle-btn"
          title={`Switch to ${mapStyleName === "dark" ? "Light" : "Dark"} map`}
        >
          <span className="text-[#00FF94]">{mapStyleName === "dark" ? "Lt" : "Dk"}</span>
        </button>
        <button
          onClick={() => navigate("/submit-atm")}
          className="w-12 h-12 rounded-full bg-[#00FF94] flex items-center justify-center shadow-lg hover:bg-[#00FF94]/90 transition-colors"
          data-testid="submit-atm-fab"
        >
          <Plus className="w-5 h-5 text-black" strokeWidth={2} />
        </button>
        <button
          onClick={() => {
            fetchAtms();
            toast.success("Map refreshed");
          }}
          className="w-12 h-12 rounded-full glass-card flex items-center justify-center shadow-lg hover:border-white/20 transition-colors"
          data-testid="refresh-map-btn"
        >
          <RefreshCw className="w-5 h-5 text-[#00FF94]" strokeWidth={1.5} />
        </button>
        <button
          onClick={() => {
            if (userPos && mapRef.current) {
              mapRef.current.flyTo({ center: [userPos[1], userPos[0]], zoom: 14, duration: 900 });
            } else {
              requestLocation({ promptOnError: true, recenter: true });
            }
          }}
          className="w-12 h-12 rounded-full glass-card flex items-center justify-center shadow-lg hover:border-white/20 transition-colors"
          data-testid="locate-me-btn"
        >
          <Navigation className="w-5 h-5 text-[#00FF94]" strokeWidth={1.5} />
        </button>
      </div>

      {/* ATM Count Badge */}
      <div className="fixed top-20 left-4 z-10 glass-card px-3 py-1.5 text-xs text-zinc-400 font-medium" data-testid="atm-count-badge">
        {locationStatus === "granted" && nearestKm != null ? (
          <>
            <span className="text-white font-bold">{atms.length}</span> ATMs
            <span className="text-zinc-500"> · nearest </span>
            <span className="text-[#00FF94] font-semibold">
              {nearestKm < 1 ? `${Math.round(nearestKm * 1000)} m` : `${nearestKm.toFixed(1)} km`}
            </span>
            <span className="text-zinc-500"> away</span>
          </>
        ) : (
          <>
            <span className="text-white font-bold">{totalAtmCount ?? atms.length}</span> ATMs
            <button
              onClick={() => requestLocation({ promptOnError: true, recenter: true })}
              className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#00FF94] hover:text-white transition-colors"
              data-testid="use-my-location-btn"
              title="Find ATMs near you"
            >
              <Navigation className="w-3 h-3" strokeWidth={2} />
              {locationStatus === "pending" ? "Locating…" : "Use my location"}
            </button>
          </>
        )}
      </div>

      {/* ATM Detail Sheet */}
      {selectedAtm && (
        <ATMDetailSheet
          atm={selectedAtm}
          onClose={() => {
            setSelectedAtm(null);
            clearSheetPadding();
          }}
          user={user}
          onReportSubmitted={fetchAtms}
          myCircles={myCircles}
        />
      )}

      <NotificationsPane open={notifOpen} onOpenChange={setNotifOpen} onRead={refreshUnread} />

      <LocationPrompt
        open={locationPromptOpen}
        onOpenChange={setLocationPromptOpen}
        errorCode={locationErrorCode}
        retrying={locationStatus === "pending"}
        onRetry={() => requestLocation({ promptOnError: true, recenter: true })}
      />

      <HelpButton screen="map" tips={MAP_HELP} visible={!selectedAtm} position="left" />
      <BottomNav active="map" />
    </div>
  );
}
